import { useState } from 'react';
import { toast } from 'react-toastify';
import { Orden } from '../../services/ordenService';
import { ticketService } from '../../services/ticketService';

interface PagoModalProps {
  orden: Orden;
  onClose: () => void;
  onPagar: (pagos: Array<{ tipoPago: string; monto: number; referencia?: string }>) => void;
}

const PagoModal = ({ orden, onClose, onPagar }: PagoModalProps) => {
  const [pagos, setPagos] = useState<Array<{ tipoPago: string; monto: number; referencia?: string }>>([
    { tipoPago: 'efectivo', monto: orden.total }
  ]);

  const tiposPago = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'vale', label: 'Vale' },
    { value: 'otros', label: 'Otros' }
  ];

  const totalPagado = pagos.reduce((sum, p) => sum + p.monto, 0);
  const faltante = orden.total - totalPagado;
  const cambio = totalPagado > orden.total ? totalPagado - orden.total : 0;

  const handleAgregarPago = () => {
    setPagos([...pagos, { tipoPago: 'efectivo', monto: 0 }]);
  };

  const handleEliminarPago = (index: number) => {
    setPagos(pagos.filter((_, i) => i !== index));
  };

  const handleActualizarPago = (index: number, field: string, value: any) => {
    setPagos(pagos.map((pago, i) =>
      i === index ? { ...pago, [field]: value } : pago
    ));
  };

  const [imprimirTicket, setImprimirTicket] = useState(true);

  const handlePagar = async () => {
    if (totalPagado < orden.total) {
      alert(`Falta $${faltante.toFixed(2)} para completar el pago`);
      return;
    }

    onPagar(pagos);
    
    // Imprimir ticket automáticamente si está habilitado
    if (imprimirTicket) {
      try {
        await ticketService.imprimirTicket(orden.id, 'html');
      } catch (error) {
        console.error('Error al imprimir ticket:', error);
        toast.warning('No se pudo imprimir el ticket automáticamente');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Procesar Pago</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-4 bg-gray-50 rounded">
          <div className="text-sm text-gray-600">Orden #{orden.numeroOrden}</div>
          <div className="text-2xl font-bold">${orden.total.toFixed(2)}</div>
        </div>

        <div className="space-y-4">
          {pagos.map((pago, index) => (
            <div key={index} className="flex space-x-2 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Pago
                </label>
                <select
                  value={pago.tipoPago}
                  onChange={(e) => handleActualizarPago(index, 'tipoPago', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {tiposPago.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={pago.monto}
                  onChange={(e) => handleActualizarPago(index, 'monto', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {pago.tipoPago !== 'efectivo' && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referencia
                  </label>
                  <input
                    type="text"
                    value={pago.referencia || ''}
                    onChange={(e) => handleActualizarPago(index, 'referencia', e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Número de referencia"
                  />
                </div>
              )}
              {pagos.length > 1 && (
                <button
                  onClick={() => handleEliminarPago(index)}
                  className="px-3 py-2 text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          <button
            onClick={handleAgregarPago}
            className="text-sm text-primary-600 hover:text-primary-900"
          >
            + Agregar otro método de pago
          </button>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded space-y-2">
          <div className="flex justify-between text-sm">
            <span>Total Orden:</span>
            <span>${orden.total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Total Pagado:</span>
            <span>${totalPagado.toFixed(2)}</span>
          </div>
          {faltante > 0 && (
            <div className="flex justify-between text-sm text-red-600">
              <span>Faltante:</span>
              <span>${faltante.toFixed(2)}</span>
            </div>
          )}
          {cambio > 0 && (
            <div className="flex justify-between text-sm text-green-600 font-bold">
              <span>Cambio:</span>
              <span>${cambio.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={imprimirTicket}
              onChange={(e) => setImprimirTicket(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Imprimir ticket automáticamente</span>
          </label>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => ticketService.verTicket(orden.id, 'html').catch(() => toast.error('Error al generar ticket'))}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Ver Ticket
          </button>
          <button
            onClick={handlePagar}
            disabled={totalPagado < orden.total}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            Procesar Pago
          </button>
        </div>
      </div>
    </div>
  );
};

export default PagoModal;

